import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useSearch } from '../context/SearchContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './HotelDetail.css';

const HotelDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { searchState } = useSearch();

  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState(null);

  useEffect(() => {
    const fetchHotel = async () => {
      try {
        const res = await axios.get(`/api/hotels/${id}`);
        setHotel(res.data.hotel);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchHotel();
    window.scrollTo(0, 0);
  }, [id]);

  const handleBookRoom = (room) => {
    if (!user) {
      navigate('/login', { state: { from: `/hotels/${id}` } });
      return;
    }
    navigate(`/booking/${hotel._id}/${room._id}`, {
      state: { hotel, room, searchState }
    });
  };

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < Math.floor(rating) ? 'filled' : ''}`}>★</span>
    ));

  if (loading) return (
    <div>
      <Navbar />
      <div className="page-loader" style={{ marginTop: 80 }}>
        <div className="spinner" />
        <p>Loading property details...</p>
      </div>
    </div>
  );

  if (!hotel) return (
    <div>
      <Navbar />
      <div className="page-loader" style={{ marginTop: 80 }}>
        <p><i class="fa-regular fa-face-frown-open"></i>Hotel not found</p>
      </div>
    </div>
  );

  return (
    <div className="hotel-detail">
      <Navbar />

      <div className="detail-container container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <span onClick={() => navigate('/')} className="bc-link">Home</span>
          <span className="bc-sep">›</span>
          <span onClick={() => navigate('/hotels')} className="bc-link">Hotels</span>
          <span className="bc-sep">›</span>
          <span className="bc-current">{hotel.name}</span>
        </div>

        {/* Title area */}
        <div className="detail-header">
          <div>
            <div className="detail-type-row">
              <span className="badge badge-accent">{hotel.type}</span>
              {hotel.featured && <span className="badge badge-green"> Featured</span>}
            </div>
            <h1 className="detail-name">{hotel.name}</h1>
            <div className="detail-meta">
              <div className="detail-rating">
                <div className="stars">{renderStars(hotel.rating)}</div>
                <span className="rating-val">{hotel.rating?.toFixed(1)}</span>
                <span className="review-ct">({hotel.reviewCount?.toLocaleString()} reviews)</span>
              </div>
              <div className="detail-location">
                <span>📍</span>
                <span>{hotel.address}, {hotel.city}, {hotel.country}</span>
              </div>
            </div>
          </div>
          <div className="detail-price-box">
            <span className="detail-price-from">Starting from</span>
            <span className="detail-price">${hotel.cheapestPrice}</span>
            <span className="detail-price-night">/night</span>
          </div>
        </div>

        {/* Gallery */}
        <div className="gallery">
          <div className="gallery-main">
            <img
              src={hotel.photos?.[activePhoto] || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=900'}
              alt={hotel.name}
            />
          </div>
          {hotel.photos?.length > 1 && (
            <div className="gallery-thumbs">
              {hotel.photos.map((photo, i) => (
                <button
                  key={i}
                  className={`gallery-thumb ${activePhoto === i ? 'active' : ''}`}
                  onClick={() => setActivePhoto(i)}
                >
                  <img src={photo} alt={`${hotel.name} ${i + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="detail-content">
          {/* Left */}
          <div className="detail-left">
            {/* Description */}
            <section className="detail-section">
              <h2 className="detail-section-title">About This Property</h2>
              <p className="detail-description">{hotel.description}</p>
              <div className="detail-distance">
                <span><i class="fa-solid fa-car-side"></i></span>
                <span>{hotel.distance}</span>
              </div>
            </section>

            {/* Amenities */}
            <section className="detail-section">
              <h2 className="detail-section-title">Amenities</h2>
              <div className="amenities-grid">
                {hotel.amenities?.map((amenity, i) => (
                  <div key={i} className="amenity-item">
                    <span className="amenity-check">✓</span>
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Rooms */}
            <section className="detail-section">
              <h2 className="detail-section-title">Available Rooms</h2>
              <div className="rooms-list">
                {hotel.rooms?.map((room) => (
                  <div
                    key={room._id}
                    className={`room-card ${selectedRoom?._id === room._id ? 'room-selected' : ''}`}
                    onClick={() => setSelectedRoom(room)}
                  >
                    <div className="room-card-content">
                      <div className="room-info">
                        <h3 className="room-title">{room.title}</h3>
                        <p className="room-desc">{room.description}</p>
                        <div className="room-meta">
                          <span className="room-meta-item">
                            <span><i class="fa-solid fa-people-line"></i></span> Max {room.maxPeople} guests
                          </span>
                          <span className="room-meta-item">
                            <span><i class="fa-solid fa-door-closed"></i></span> {room.roomNumbers?.length} rooms available
                          </span>
                        </div>
                        {room.amenities?.length > 0 && (
                          <div className="room-amenities">
                            {room.amenities.slice(0, 4).map((a, i) => (
                              <span key={i} className="amenity-tag">{a}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="room-price-col">
                        <div className="room-price">${room.price}</div>
                        <div className="room-per-night">per night</div>
                        <button
                          className={`btn btn-dark btn-sm room-book-btn ${selectedRoom?._id === room._id ? 'selected' : ''}`}
                          onClick={(e) => { e.stopPropagation(); handleBookRoom(room); }}
                        >
                          {selectedRoom?._id === room._id ? '✓ Selected' : 'Select Room'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right – Booking sidebar */}
          <div className="detail-right">
            <div className="booking-sidebar">
              <div className="booking-sidebar-header">
                <h3>Book Your Stay</h3>
                <div className="sidebar-price">
                  <span className="sidebar-amount">${hotel.cheapestPrice}</span>
                  <span className="sidebar-per">/ night</span>
                </div>
              </div>
              <div className="sidebar-dates">
                <div className="sidebar-date">
                  <label>Check-in</label>
                  <p>{format(searchState.checkIn || new Date(), 'EEE, MMM d')}</p>
                </div>
                <div className="sidebar-arrow">→</div>
                <div className="sidebar-date">
                  <label>Check-out</label>
                  <p>{format(searchState.checkOut || new Date(Date.now() + 172800000), 'EEE, MMM d')}</p>
                </div>
              </div>
              <div className="sidebar-guests">
                <label>Guests</label>
                <p>{(searchState.adults || 2) + (searchState.children || 0)} guests · {searchState.rooms || 1} room</p>
              </div>

              {selectedRoom ? (
                <div className="sidebar-room-selected">
                  <p className="sidebar-room-label">Selected:</p>
                  <p className="sidebar-room-name">{selectedRoom.title}</p>
                </div>
              ) : (
                <p className="sidebar-hint">↑ Select a room above to continue</p>
              )}

              <button
                className="btn btn-primary btn-lg sidebar-book-btn"
                disabled={!selectedRoom}
                onClick={() => selectedRoom && handleBookRoom(selectedRoom)}
              >
                {selectedRoom ? 'Reserve Now →' : 'Select a Room First'}
              </button>

              <div className="sidebar-perks">
                <div className="sidebar-perk">✓ Free cancellation on most rooms</div>
                <div className="sidebar-perk">✓ No booking fees</div>
                <div className="sidebar-perk">✓ Best price guarantee</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HotelDetail;
